import {
  CHUNK_PAYLOAD_BYTES,
  decodeChunk,
  encodeChunk,
  segmentKey,
  splitIntoChunks,
  type ControlMsg,
  type GeoHint,
  type PeerId,
  type SegmentId,
} from "@p2p-hls/protocol";
import { Emitter } from "./emitter.js";
import type { IPeer, StridedResult } from "./types.js";

/** EWMA smoothing factor for RTT. Higher = more reactive to recent probes. */
const RTT_ALPHA = 0.3;
/** DataChannel buffered-amount ceiling before we pause sending (backpressure). */
const SEND_HIGH_WATER = 1 << 20; // 1 MB
const PING_INTERVAL_MS = 2000;
const PING_STALE_MS = 30_000; // drop outstanding pings whose pong never came

interface Pending {
  readonly seg: SegmentId;
  readonly startMs: number;
  readonly stride: number;
  readonly offset: number;
  chunks: Map<number, Uint8Array>;
  received: number;
  receivedBytes: number;
  total: number; // -1 until first frame / done
  doneCount: number; // -1 until the `done` control arrives
  resolve: (result: StridedResult) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

type PeerEvents = {
  /** Segments the remote peer just advertised (empty on channel open). */
  have: SegmentId[];
  close: void;
};

export interface PeerOptions {
  /** Called with each fresh RTT sample (ms) so metrics can build a distribution. */
  onRtt?: (peerId: PeerId, ms: number) => void;
  /** TEST-ONLY: artificial delay (ms) before serving a segment (slow-seeder sim). */
  serveDelayMs?: number;
}

/**
 * One WebRTC peer connection implementing the transport-agnostic IPeer.
 *
 * Carries both control (JSON strings) and data (binary chunk frames) on a
 * single reliable, UNORDERED DataChannel (ordering is unnecessary — chunks carry
 * their index — and dropping it removes head-of-line blocking). Reassembles
 * chunked segments by the requester-chosen reqId.
 */
export class Peer extends Emitter<PeerEvents> implements IPeer {
  readonly id: PeerId;
  readonly geo: GeoHint;

  private readonly pc: RTCPeerConnection;
  private channel: RTCDataChannel | null = null;
  /** key -> full SegmentId, so the prefetcher can pull upcoming segments. */
  private readonly remoteHave = new Map<string, SegmentId>();
  private readonly pending = new Map<number, Pending>();
  private nextReqId = 1;
  private rttMs = Infinity;
  private bwBps = 0; // EWMA download throughput from this peer (bytes/sec)
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pingSeq = 0;
  private readonly outstandingPings = new Map<number, number>();
  private closed = false;
  /** True once the selected ICE candidate pair is detected to use a TURN relay. */
  private relayed = false;

  constructor(
    id: PeerId,
    geo: GeoHint,
    pc: RTCPeerConnection,
    /** Serve a segment we hold to a requesting peer, or undefined if we lack it. */
    private readonly serve: (seg: SegmentId) => Uint8Array | undefined,
    private readonly options: PeerOptions = {},
  ) {
    super();
    this.id = id;
    this.geo = geo;
    this.pc = pc;
    pc.addEventListener("connectionstatechange", () => {
      if (pc.connectionState === "connected") void this.detectRelay();
      if (["disconnected", "failed", "closed"].includes(pc.connectionState)) this.close();
    });
  }

  /** True if this peer is reached via a TURN relay (highest-latency path). Used by
   *  the registry to deprioritize relayed peers, especially on mobile. */
  isRelayed(): boolean {
    return this.relayed;
  }

  /** Inspect the selected ICE candidate pair once on connect; flag TURN relay. */
  private async detectRelay(): Promise<void> {
    try {
      const stats = await this.pc.getStats();
      let pair: RTCIceCandidatePairStats | undefined;
      stats.forEach((r) => {
        const s = r as RTCIceCandidatePairStats & { selected?: boolean; nominated?: boolean };
        if (s.type === "candidate-pair" && (s.selected || (s.nominated && s.state === "succeeded"))) pair = s;
      });
      if (!pair) return;
      const kind = (id?: string): string | undefined =>
        id ? (stats.get(id) as { candidateType?: string } | undefined)?.candidateType : undefined;
      this.relayed = kind(pair.localCandidateId) === "relay" || kind(pair.remoteCandidateId) === "relay";
    } catch {
      /* getStats shape varies across browsers — default to not-relayed */
    }
  }

  // ---- lifecycle ------------------------------------------------------------

  /** Attach the DataChannel (created by us as offerer, or received as answerer). */
  attachChannel(channel: RTCDataChannel): void {
    channel.binaryType = "arraybuffer";
    this.channel = channel;
    channel.addEventListener("open", () => {
      this.startPinging();
      this.emit("have", []); // signal channel-open (peer-manager listens for this)
    });
    channel.addEventListener("close", () => this.close());
    channel.addEventListener("message", (ev) => this.onMessage(ev.data));
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    if (this.pingTimer) clearInterval(this.pingTimer);
    for (const p of this.pending.values()) {
      clearTimeout(p.timer);
      p.reject(new Error("peer closed"));
    }
    this.pending.clear();
    try {
      this.channel?.close();
    } catch {
      /* ignore */
    }
    try {
      this.pc.close();
    } catch {
      /* ignore */
    }
    this.emit("close", undefined);
  }

  // ---- IPeer ----------------------------------------------------------------

  rtt(): number {
    return this.rttMs;
  }

  /** Smoothed download throughput from this peer, bytes/sec (0 until measured). */
  bandwidth(): number {
    return this.bwBps;
  }

  has(seg: SegmentId): boolean {
    return this.remoteHave.has(segmentKey(seg));
  }

  /** All segments this peer currently advertises (for the prefetcher). */
  advertised(): SegmentId[] {
    return [...this.remoteHave.values()];
  }

  inflight(): number {
    return this.pending.size;
  }

  /** Request the whole segment (stride 1) and reassemble it. */
  async request(seg: SegmentId, timeoutMs: number): Promise<Uint8Array> {
    const { entries } = await this.requestStrided(seg, 1, 0, timeoutMs);
    const ordered = entries.sort((a, b) => a[0] - b[0]).map((e) => e[1]);
    return concat(ordered, ordered.reduce((n, c) => n + c.byteLength, 0));
  }

  /**
   * Request only the chunks with `index % stride === offset`. The multi-peer
   * scheduler assigns each peer a distinct offset so a segment downloads in
   * parallel across the swarm.
   */
  requestStrided(seg: SegmentId, stride: number, offset: number, timeoutMs: number): Promise<StridedResult> {
    if (!this.isReady()) return Promise.reject(new Error("channel not open"));
    const reqId = this.nextReqId++;
    return new Promise<StridedResult>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(reqId);
        this.sendControl({ t: "cancel", reqId });
        reject(new Error("peer request timeout"));
      }, timeoutMs);
      this.pending.set(reqId, {
        seg,
        startMs: now(),
        stride,
        offset,
        chunks: new Map(),
        received: 0,
        receivedBytes: 0,
        total: -1,
        doneCount: -1,
        resolve,
        reject,
        timer,
      });
      const msg: { t: "request"; reqId: number; seg: SegmentId; stride?: number; offset?: number } = { t: "request", reqId, seg };
      if (stride !== 1 || offset !== 0) {
        msg.stride = stride;
        msg.offset = offset;
      }
      this.sendControl(msg);
    });
  }

  private maybeComplete(reqId: number, p: Pending): void {
    if (p.doneCount < 0 || p.received < p.doneCount) return;
    clearTimeout(p.timer);
    this.pending.delete(reqId);
    // Throughput EWMA (bytes/sec) from this transfer.
    const elapsed = Math.max(1, now() - p.startMs);
    const sample = (p.receivedBytes / elapsed) * 1000;
    this.bwBps = this.bwBps === 0 ? sample : 0.3 * sample + 0.7 * this.bwBps;
    p.resolve({ total: p.total, entries: [...p.chunks.entries()] });
  }

  cancel(seg: SegmentId): void {
    const key = segmentKey(seg);
    for (const [reqId, p] of this.pending) {
      if (segmentKey(p.seg) === key) {
        clearTimeout(p.timer);
        this.pending.delete(reqId);
        this.sendControl({ t: "cancel", reqId });
      }
    }
  }

  // ---- HAVE announcements ---------------------------------------------------

  announceHave(add: SegmentId[], drop: SegmentId[] = []): void {
    if (add.length === 0 && drop.length === 0) return;
    this.sendControl({ t: "have", add, ...(drop.length ? { drop } : {}) });
  }

  // ---- messaging ------------------------------------------------------------

  private isReady(): boolean {
    return !this.closed && this.channel?.readyState === "open";
  }

  private sendControl(msg: ControlMsg): void {
    if (this.isReady()) this.channel!.send(JSON.stringify(msg));
  }

  private onMessage(data: string | ArrayBuffer): void {
    if (typeof data === "string") {
      this.onControl(JSON.parse(data) as ControlMsg);
    } else {
      this.onData(data);
    }
  }

  private onControl(msg: ControlMsg): void {
    switch (msg.t) {
      case "have": {
        for (const s of msg.add) this.remoteHave.set(segmentKey(s), s);
        for (const s of msg.drop ?? []) this.remoteHave.delete(segmentKey(s));
        this.emit("have", msg.add);
        break;
      }
      case "request":
        void this.onRequest(msg.reqId, msg.seg, msg.stride ?? 1, msg.offset ?? 0);
        break;
      case "done": {
        const p = this.pending.get(msg.reqId);
        if (p) {
          p.total = msg.total;
          p.doneCount = msg.count;
          this.maybeComplete(msg.reqId, p);
        }
        break;
      }
      case "reject": {
        const p = this.pending.get(msg.reqId);
        if (p) {
          clearTimeout(p.timer);
          this.pending.delete(msg.reqId);
          p.reject(new Error(`peer reject: ${msg.reason}`));
        }
        break;
      }
      case "cancel":
        // Requester aborted; nothing to clean up since we stream synchronously.
        break;
      case "ping":
        this.sendControl({ t: "pong", id: msg.id, ts: msg.ts });
        break;
      case "pong": {
        const sentAt = this.outstandingPings.get(msg.id);
        if (sentAt !== undefined) {
          this.outstandingPings.delete(msg.id);
          const sample = now() - sentAt;
          this.rttMs = this.rttMs === Infinity ? sample : RTT_ALPHA * sample + (1 - RTT_ALPHA) * this.rttMs;
          this.options.onRtt?.(this.id, sample);
        }
        break;
      }
    }
  }

  /** Serve a request: stream only the chunks for `offset` (stride assignment). */
  private async onRequest(reqId: number, seg: SegmentId, stride: number, offset: number): Promise<void> {
    const bytes = this.serve(seg);
    if (!bytes) {
      this.sendControl({ t: "reject", reqId, reason: "missing" });
      return;
    }
    // TEST-ONLY slow-seeder simulation (benchmark harness).
    if (this.options.serveDelayMs) await sleep(this.options.serveDelayMs);
    if (!this.isReady()) return;
    const chunks = splitIntoChunks(bytes);
    let count = 0;
    for (let i = offset; i < chunks.length; i += stride) {
      if (!this.isReady()) return;
      await this.waitForDrain();
      const frame = encodeChunk({ reqId, index: i, total: chunks.length, byteLength: bytes.byteLength }, chunks[i]!);
      this.channel!.send(frame);
      count++;
    }
    // Tell the requester the transfer is complete (robust on an unordered channel).
    this.sendControl({ t: "done", reqId, total: chunks.length, count });
  }

  private onData(data: ArrayBuffer): void {
    const { header, payload } = decodeChunk(data);
    const p = this.pending.get(header.reqId);
    if (!p) return; // cancelled / already resolved
    if (p.total === -1) p.total = header.total;
    if (!p.chunks.has(header.index)) {
      p.chunks.set(header.index, payload);
      p.received++;
      p.receivedBytes += payload.byteLength;
    }
    this.maybeComplete(header.reqId, p);
  }

  // ---- flow control ---------------------------------------------------------

  private waitForDrain(): Promise<void> {
    const ch = this.channel!;
    if (ch.bufferedAmount < SEND_HIGH_WATER) return Promise.resolve();
    return new Promise<void>((resolve) => {
      ch.bufferedAmountLowThreshold = SEND_HIGH_WATER / 2;
      const onLow = () => {
        ch.removeEventListener("bufferedamountlow", onLow);
        resolve();
      };
      ch.addEventListener("bufferedamountlow", onLow);
    });
  }

  private startPinging(): void {
    if (this.pingTimer) return;
    const ping = () => {
      if (!this.isReady()) return;
      const ts = now();
      // Reap pings whose pong never arrived so the map can't grow unbounded.
      for (const [id, sentAt] of this.outstandingPings) {
        if (ts - sentAt > PING_STALE_MS) this.outstandingPings.delete(id);
      }
      const id = ++this.pingSeq;
      this.outstandingPings.set(id, ts);
      this.sendControl({ t: "ping", id, ts });
    };
    ping();
    this.pingTimer = setInterval(ping, PING_INTERVAL_MS);
  }
}

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

function concat(chunks: Uint8Array[], byteLength: number): Uint8Array {
  const out = new Uint8Array(byteLength);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.byteLength;
  }
  return out;
}

export { CHUNK_PAYLOAD_BYTES };
